import React from 'react';
import { t } from '@lingui/core/macro';

import AlertInfo from '@rover/icons/svg/alert-info.svg';
import { Button, Flex, Text } from '@rover/kibble/core';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import type { EnhancedPersonSitterProfileServiceAddOn } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileServiceAddOn';
import useIsBHUser from '@rover/shared/js/hooks/useIsBHUser';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { getIsPriceTransparencyFull } from '@rover/utilities/priceTransparency';

import { formatPrice } from './utils';

const hasSecondaryText = (sa: EnhancedPersonSitterProfileServiceAddOn): boolean => {
  if (sa.slug === 'extended-care') return true;
  if (sa.slug === 'short-notice') return true;
  return sa.pricePerUnitObj.amount !== '0';
};

type Props = {
  addOns: EnhancedPersonSitterProfileServiceAddOn[];
  acceptCats: boolean;
  onMoreInfoClick?: (content: EnhancedPersonSitterProfileServiceAddOn) => void;
  priceTransparencyLevel?: PriceTransparencyLevel;
  showSittersRates?: boolean;
};

const ServiceAddOns = ({
  addOns,
  acceptCats,
  onMoreInfoClick,
  priceTransparencyLevel,
  showSittersRates = true,
}: Props): JSX.Element => {
  const { locale } = useI18nContext();
  const isBHUser = useIsBHUser();

  const CAT_RATES = ['additional-cat', 'cat-care'];
  return (
    <Flex flexDirection="column" pt="6x" data-testid="service-detail-addons">
      {addOns.map((addOn, i) => {
        if (!acceptCats && CAT_RATES.includes(addOn.slug)) return null;

        const price = formatPrice(
          addOn,
          locale,
          getIsPriceTransparencyFull(priceTransparencyLevel) || isBHUser,
          !showSittersRates && isBHUser ? addOn.priceInCredits : null
        );
        return (
          <Flex justifyContent="space-between" key={addOn.slug} pt={i === 0 ? '0x' : '4x'}>
            <Flex alignItems="start">
              <Text size="100">{addOn.nameDesc}</Text>
              {addOn.helpText && (
                <Flex>
                  <Button
                    variant="flat"
                    onClick={() => {
                      onMoreInfoClick && onMoreInfoClick(addOn);
                    }}
                    p="0x"
                    ml="2x"
                    data-testid="help-text-button"
                    aria-label={t`More information`}
                    sx={{ border: 0 }}
                  >
                    <AlertInfo width={16} height={16} />
                  </Button>
                </Flex>
              )}
            </Flex>
            <Flex flexDirection="column" alignItems="end" webProps={{ role: 'group' }}>
              <Text size="100" fontWeight="semibold">
                {price}
              </Text>
              {hasSecondaryText(addOn) && (
                <Text size="100" textColor="secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {addOn.unitAsSentence}
                </Text>
              )}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default ServiceAddOns;
